// /src/ui/layout/PublicShell.tsx
import { useEffect, useState } from "react";
import { useBranding } from "../../core/useBranding";
import { getMeSafe, type Me } from "../../core/me";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { getProfileSummaryForIdentity,
  tryApplyCachedThemeForIdentity
 } from "../../modules/profileSummary";

export function PublicShell(props: {
  children: (ctx: { authed: boolean; email?: string }) => React.ReactNode;
}) {
  const brand = useBranding();
  const [me, setMe] = useState<Me>({ authenticated: false });

  const [accountLabel, setAccountLabel] = useState<string | undefined>(undefined);
  const [authStatus, setAuthStatus] = useState<"loading" | "authed" | "anon">("loading");

  useEffect(() => {
    let alive = true;

    (async () => {
      const m = await getMeSafe();
      if (!alive) return;
      setMe(m);
      setAuthStatus(m.authenticated ? "authed" : "anon");

      if (m.authenticated) {
        try {
          const summary = await getProfileSummaryForIdentity(m);
          if (!alive) return;

          // Apply cached theme ASAP if authed
          tryApplyCachedThemeForIdentity(m);
          const label =
            summary?.displayName?.trim()
              ? summary.displayName
              : (m.email ?? undefined);
          setAccountLabel(label);
        } catch {
          setAccountLabel(m.email ?? undefined);
        }
      } else {
        setAccountLabel(undefined);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const authed = !!me.authenticated;
  const email = me.email ?? undefined;

  return (
    <div className="min-h-screen bg-bg text-fg">
      <Header
        brandName={brand.brandName}
        tagline={brand.tagline}
        logoSrc={brand.logoSrc32}
        authed={authed}
        authStatus={authStatus}
        accountLabel={accountLabel ?? email}
      />

      {props.children({ authed, email })}

      <Footer brandName={brand.brandName} />
    </div>
  );
}
